import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useProfile } from "@/context/ProfileContext";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import AdSlot from "@/components/AdSlot";
import { IMG } from "@/lib/images";

const CAT_LABELS = {
  lipstick: ["Rossetti", "Lipstick"], lip_gloss: ["Lip Gloss", "Lip Gloss"], lip_oil: ["Lip Oil", "Lip Oil"],
  lip_liner: ["Matite Labbra", "Lip Liner"], foundation: ["Fondotinta", "Foundation"], concealer: ["Correttori", "Concealer"],
  powder: ["Ciprie", "Powder"], blush: ["Blush", "Blush"], bronzer: ["Bronzer", "Bronzer"], highlighter: ["Illuminanti", "Highlighter"],
  eyeshadow: ["Ombretti", "Eyeshadow"], palette: ["Palette", "Palette"], mascara: ["Mascara", "Mascara"], eyeliner: ["Eyeliner", "Eyeliner"], brows: ["Sopracciglia", "Brows"],
  cleanser: ["Detergenti", "Cleanser"], toner: ["Tonici", "Toner"], serum: ["Sieri", "Serum"], moisturizer: ["Creme", "Moisturizer"],
  spf: ["SPF", "SPF"], eye_cream: ["Contorno occhi", "Eye Cream"], treatment: ["Trattamenti", "Treatment"], mask: ["Maschere", "Mask"],
  tshirt: ["T-shirt", "T-shirt"], shirt: ["Camicie", "Shirt"], top: ["Top", "Top"], hoodie: ["Felpe", "Hoodie"], jacket: ["Giacche", "Jacket"],
  coat: ["Cappotti", "Coat"], jeans: ["Jeans", "Jeans"], trousers: ["Pantaloni", "Trousers"], skirt: ["Gonne", "Skirt"], dress: ["Abiti", "Dress"],
  suit: ["Tailleur", "Suit"], shoes: ["Scarpe", "Shoes"], sneakers: ["Sneakers", "Sneakers"], bag: ["Borse", "Bag"], watch: ["Orologi", "Watch"], accessory: ["Accessori", "Accessory"],
};

const HEADERS = {
  makeup: { t_it: "Make-up", t_en: "Make-up", d_it: "Rossetti, fondotinta, palette e molto altro dai migliori brand.", d_en: "Lipsticks, foundations, palettes and more from top brands.", img: IMG.makeup },
  skincare: { t_it: "Skincare", t_en: "Skincare", d_it: "Trova i prodotti perfetti per il tuo tipo di pelle.", d_en: "Find the perfect products for your skin type.", img: IMG.skincare },
  fashion: { t_it: "Fashion", t_en: "Fashion", d_it: "Capi e accessori nei colori giusti per la tua armocromia.", d_en: "Clothing and accessories in colors right for your season.", img: IMG.fashion },
};

export default function Catalog() {
  const { domain } = useParams();
  const { t, lang } = useI18n();
  const { profile } = useProfile();
  const [category, setCategory] = useState("");
  const [brand, setBrand] = useState("");
  const [q, setQ] = useState("");
  const [cats, setCats] = useState([]);
  const [brands, setBrands] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gender, setGender] = useState(profile?.gender === "man" ? "man" : "woman");

  const head = HEADERS[domain] || HEADERS.makeup;
  const isFashion = domain === "fashion";

  useEffect(() => {
    setCategory(""); setBrand(""); setQ("");
    if (profile?.gender === "man" || profile?.gender === "woman") setGender(profile.gender);
  }, [domain, profile]);

  useEffect(() => {
    const gq = isFashion ? `&gender=${gender}` : "";
    api.get(`/categories?domain=${domain}${gq}`).then(({ data }) => setCats(data));
    api.get(`/brands?domain=${domain}${gq}`).then(({ data }) => setBrands(data));
  }, [domain, isFashion, gender]);

  useEffect(() => {
    if (isFashion) { setCategory(""); setBrand(""); }
  }, [gender, isFashion]);

  useEffect(() => {
    setLoading(true);
    const effProfile = isFashion ? { ...profile, gender } : profile;
    api.post("/recommend", { profile: effProfile, domain, category: category || undefined, limit: 100 })
      .then(({ data }) => setItems(data.items))
      .finally(() => setLoading(false));
  }, [domain, category, profile, isFashion, gender]);

  const filtered = useMemo(() => {
    return items.filter((p) => {
      if (brand && p.brand !== brand) return false;
      if (q) {
        const hay = `${p.brand} ${p.name} ${p.name_en} ${p.shade || ""}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [items, brand, q]);

  return (
    <div>
      {/* header */}
      <div className="relative h-56 md:h-64 overflow-hidden">
        <img src={head.img} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal/70 to-charcoal/20" />
        <div className="absolute inset-0 max-w-7xl mx-auto px-5 md:px-8 flex flex-col justify-center text-white">
          <h1 className="font-serif text-4xl sm:text-5xl">{lang === "it" ? head.t_it : head.t_en}</h1>
          <p className="mt-2 max-w-lg text-white/85">{lang === "it" ? head.d_it : head.d_en}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        {/* gender toggle (fashion only) */}
        {isFashion && (
          <div className="flex justify-center mb-8" data-testid="fashion-gender-toggle">
            <div className="inline-flex rounded-full border border-rose-100 bg-white p-1 shadow-sm">
              <button
                onClick={() => setGender("woman")}
                data-testid="gender-woman"
                aria-pressed={gender === "woman"}
                className={`px-8 py-3 rounded-full text-sm md:text-base font-medium transition-colors ${gender === "woman" ? "bg-charcoal text-white" : "text-charcoal/70 hover:text-charcoal"}`}
              >
                {lang === "it" ? "Donna" : "Women"}
              </button>
              <button
                onClick={() => setGender("man")}
                data-testid="gender-man"
                aria-pressed={gender === "man"}
                className={`px-8 py-3 rounded-full text-sm md:text-base font-medium transition-colors ${gender === "man" ? "bg-charcoal text-white" : "text-charcoal/70 hover:text-charcoal"}`}
              >
                {lang === "it" ? "Uomo" : "Men"}
              </button>
            </div>
          </div>
        )}

        {/* controls */}
        <div className="flex flex-col md:flex-row gap-3 md:items-center mb-6">
          <div className="relative flex-1">
            <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca prodotto o brand..." data-testid="catalog-search" className="w-full pl-10 pr-4 py-3 rounded-full border border-rose-100 focus:outline-none focus:border-rose-300" />
          </div>
          <div className="flex items-center gap-2">
            <SlidersHorizontal size={16} className="text-charcoal/40" />
            <select value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="catalog-brand" className="py-3 px-4 rounded-full border border-rose-100 bg-white text-sm focus:outline-none">
              <option value="">{t("common.brand")}: {t("common.all")}</option>
              {brands.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
        </div>

        {/* category chips */}
        <div className="flex gap-2 overflow-x-auto pb-3 mb-6" data-testid="category-chips">
          <button onClick={() => setCategory("")} className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${!category ? "bg-charcoal text-white" : "bg-surface border border-rose-100"}`}>{t("common.all")}</button>
          {cats.map((c) => (
            <button key={c} onClick={() => setCategory(c)} data-testid={`cat-${c}`} className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${category === c ? "bg-charcoal text-white" : "bg-surface border border-rose-100 hover:border-rose-300"}`}>
              {CAT_LABELS[c] ? CAT_LABELS[c][lang === "it" ? 0 : 1] : c}
            </button>
          ))}
        </div>

        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-charcoal/50">{filtered.length} prodotti</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 12 }).map((_, i) => <div key={i} className="aspect-[3/4] rounded-xl bg-surface animate-pulse" />)}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="catalog-grid">
            {filtered.map((p, i) => (
              <React.Fragment key={p.sku}>
                <ProductCard product={p} index={i} />
                {i === 7 && <div className="col-span-2 md:col-span-4"><AdSlot format="leaderboard" /></div>}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
